import * as Sentry from "@sentry/react";

/**
 * Analytics Tracker for Online Store
 * Tracks page views, clicks, purchases, and engagement metrics
 */

interface AnalyticsEvent {
  application: "online_store" | "academy" | "landing_page" | "admin";
  eventType: "page_view" | "click" | "form_submit" | "purchase" | "subscription" | "engagement";
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  pageUrl?: string;
  pagePath?: string;
  referrer?: string;
  userAgent?: string;
  userId?: number;
  sessionId: string;
  timeOnPage?: number;
  scrollDepth?: number;
  metadata?: Record<string, any>;
}

interface TrackerOptions {
  apiUrl: string;
  application: "online_store" | "academy";
  enabled?: boolean;
  batchSize?: number;
  batchInterval?: number;
  userId?: number;
}

class AnalyticsTracker {
  private apiUrl: string;
  private application: "online_store" | "academy";
  private enabled: boolean;
  private sessionId: string;
  private userId?: number;
  private eventQueue: AnalyticsEvent[] = [];
  private batchSize: number;
  private batchInterval: number;
  private flushTimer?: ReturnType<typeof setInterval>;
  private pageStartTime: number = 0;
  private maxScrollDepth: number = 0;

  constructor(options: TrackerOptions) {
    this.apiUrl = options.apiUrl;
    this.application = options.application;
    this.enabled = options.enabled !== false;
    this.sessionId = this.getOrCreateSessionId();
    this.userId = options.userId;
    this.batchSize = options.batchSize || 10;
    this.batchInterval = options.batchInterval || 5000;

    if (this.enabled) {
      this.startBatchTimer();
      // Track initial page view
      this.trackPageView();
      // Setup scroll tracking
      this.setupScrollTracking();
      // Track time on page before unload
      this.setupTimeTracking();
    }
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    const storageKey = `analytics_session_${this.application}`;
    let sessionId = sessionStorage.getItem(storageKey);
    
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }
    
    return sessionId;
  }

  /**
   * Set user ID
   */
  setUserId(userId: number) {
    this.userId = userId;
  }

  /**
   * Track a generic event
   */
  track(
    eventType: AnalyticsEvent["eventType"],
    category: string,
    action: string,
    label?: string,
    metadata?: Record<string, any>
  ) {
    if (!this.enabled) return;

    const event: AnalyticsEvent = {
      application: this.application,
      eventType,
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      pageUrl: window.location.href,
      pagePath: window.location.pathname + window.location.search,
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
      userId: this.userId,
      sessionId: this.sessionId,
      metadata: metadata || {},
    };

    this.queueEvent(event);
  }

  /**
   * Track page view
   */
  trackPageView(url?: string, metadata?: Record<string, any>) {
    if (!this.enabled) return;

    const currentUrl = url || window.location.href;
    const currentPath = url 
      ? new URL(url, window.location.origin).pathname + new URL(url, window.location.origin).search
      : window.location.pathname + window.location.search;

    const event: AnalyticsEvent = {
      application: this.application,
      eventType: "page_view",
      eventCategory: "navigation",
      eventAction: "view",
      eventLabel: currentPath,
      pageUrl: currentUrl,
      pagePath: currentPath,
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
      userId: this.userId,
      sessionId: this.sessionId,
      metadata: metadata || {},
    };

    this.queueEvent(event);
    this.pageStartTime = Date.now();
    this.maxScrollDepth = 0;
  }

  /**
   * Track click event
   */
  trackClick(
    element: HTMLElement | string,
    category?: string,
    metadata?: Record<string, any>
  ) {
    if (!this.enabled) return;

    const elementObj = typeof element === "string" 
      ? document.querySelector(element) as HTMLElement
      : element;

    if (!elementObj) return;

    const label = elementObj.textContent?.trim() || 
                  elementObj.getAttribute("aria-label") ||
                  elementObj.getAttribute("title") ||
                  "Unknown";

    this.track("click", category || "interaction", "click", label, {
      ...metadata,
      elementId: elementObj.id,
      elementClass: elementObj.className,
      elementTag: elementObj.tagName.toLowerCase(),
    });
  }

  /**
   * Track form submission
   */
  trackFormSubmit(formId: string, metadata?: Record<string, any>) {
    if (!this.enabled) return;

    this.track("form_submit", "form", "submit", formId, metadata);
  }

  /**
   * Track purchase
   */
  trackPurchase(orderId: string, amount: number, items: any[] = [], metadata?: Record<string, any>) {
    if (!this.enabled) return;

    this.track("purchase", "ecommerce", "purchase", orderId, {
      ...metadata,
      orderId,
      amount,
      itemCount: items.length,
      items: items.map(item => ({
        id: item.id || item.productId,
        name: item.name || item.productName,
        quantity: item.quantity || 1,
        price: item.price || 0,
      })),
    });
  }

  /**
   * Track engagement (time on page, scroll depth)
   */
  trackEngagement(timeOnPage?: number, scrollDepth?: number, metadata?: Record<string, any>) {
    if (!this.enabled) return;

    const event: AnalyticsEvent = {
      application: this.application,
      eventType: "engagement",
      eventCategory: "engagement",
      eventAction: "update",
      pageUrl: window.location.href,
      pagePath: window.location.pathname + window.location.search,
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
      userId: this.userId,
      sessionId: this.sessionId,
      timeOnPage: timeOnPage || Math.floor((Date.now() - this.pageStartTime) / 1000),
      scrollDepth: scrollDepth || this.maxScrollDepth,
      metadata: metadata || {},
    };

    this.queueEvent(event);
  }

  /**
   * Setup scroll depth tracking
   */
  private setupScrollTracking() {
    let scrollCheckpoints = [25, 50, 75, 100];
    let trackedCheckpoints = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

      this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollPercent);

      // Track milestone scroll depths
      scrollCheckpoints.forEach(checkpoint => {
        if (scrollPercent >= checkpoint && !trackedCheckpoints.has(checkpoint)) {
          trackedCheckpoints.add(checkpoint);
          this.track("engagement", "engagement", "scroll", `${checkpoint}%`, {
            scrollDepth: checkpoint,
          });
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  /**
   * Setup time on page tracking
   */
  private setupTimeTracking() {
    // Track engagement before page unload
    window.addEventListener("beforeunload", () => {
      if (this.pageStartTime > 0) {
        const timeOnPage = Math.floor((Date.now() - this.pageStartTime) / 1000);
        if (timeOnPage > 0) {
          this.trackEngagement(timeOnPage, this.maxScrollDepth);
          this.flushEvents(true); // Force immediate flush
        }
      }
    });

    // Track engagement every 30 seconds
    setInterval(() => {
      if (this.pageStartTime > 0) {
        const timeOnPage = Math.floor((Date.now() - this.pageStartTime) / 1000);
        if (timeOnPage >= 30) {
          this.trackEngagement(timeOnPage, this.maxScrollDepth);
        }
      }
    }, 30000);
  }

  /**
   * Queue event for batch sending
   */
  private queueEvent(event: AnalyticsEvent) {
    this.eventQueue.push(event);

    // Flush if batch size reached
    if (this.eventQueue.length >= this.batchSize) {
      this.flushEvents();
    }
  }

  /**
   * Start batch timer
   */
  private startBatchTimer() {
    this.flushTimer = setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.flushEvents();
      }
    }, this.batchInterval);
  }

  /**
   * Flush events to server
   */
  async flushEvents(sync: boolean = false): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const response = await fetch(`${this.apiUrl}/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(events),
        keepalive: sync, // Use keepalive for sync sends (like beforeunload)
      });

      if (!response.ok) {
        const errorMsg = `Analytics tracking failed: ${response.status} ${response.statusText}`;
        console.error(errorMsg);
        
        Sentry.captureException(new Error(errorMsg), {
            tags: { source: "analytics_tracker", type: "http_error" },
            extra: { 
                apiUrl: this.apiUrl, 
                statusCode: response.status,
                eventsCount: events.length 
            }
        });

        // Re-queue events on failure (with limit to prevent infinite growth)
        if (this.eventQueue.length < 100) {
          this.eventQueue.unshift(...events);
        }
      }
    } catch (error) {
      console.error("Analytics tracking error:", error);
      
      Sentry.captureException(error, {
          tags: { source: "analytics_tracker", type: "network_error" },
          extra: { 
            apiUrl: this.apiUrl,
            eventsCount: events.length
          }
      });

      // Re-queue events on failure
      if (this.eventQueue.length < 100) {
        this.eventQueue.unshift(...events);
      }
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushEvents(true);
  }
}

// Export singleton instance
let trackerInstance: AnalyticsTracker | null = null;

export function initTracker(options: TrackerOptions): AnalyticsTracker {
  if (trackerInstance) {
    trackerInstance.destroy();
  }
  trackerInstance = new AnalyticsTracker(options);
  return trackerInstance;
}

export function getTracker(): AnalyticsTracker | null {
  return trackerInstance;
}

export default AnalyticsTracker;

